import { useEffect, useMemo, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch } from '../components/ui.tsx';
import { t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { day, since } from '../lib/format.ts';
import { AccessBadge, Code, CopyButton, ModuleChips, origin, PartsPicker } from './Developer.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Table } from './Reports.tsx';

/**
 * Developer → Apps: programs other people write, which a person connects to their own account
 * (OAuth 2.0 with PKCE). The manager registers the app and the most it may ask for; each person
 * allows it on the "Allow" page; Settings → Security lists their connected apps.
 */

type Access = 'read' | 'write';
export interface AppRow {
  id: string;
  name: string;
  description: string | null;
  homepage: string | null;
  client_id: string;
  secret_prefix: string | null;
  redirect_uris: string[];
  access: Access;
  modules: string[] | null;
  user_name: string | null;
  active: boolean;
  public: boolean;
  connected: number;
  last_used_at: string | null;
  created_at: string;
}
interface Connection {
  id: string;
  access: Access;
  modules: string[] | null;
  created_at: string;
  last_used_at: string | null;
  last_ip: string | null;
  user_name: string;
}
interface Modules {
  modules: { name: string; label: string }[];
}

const LOOPBACK = new Set(['localhost', '127.0.0.1', '[::1]']);
/** The same rule as the server: https, a program on this computer, or a phone app's own scheme. */
function redirectProblem(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  let u: URL;
  try {
    u = new URL(v);
  } catch {
    return t('dev.app_uri_whole');
  }
  if (v.includes('#')) return t('dev.app_uri_hash');
  if (u.protocol === 'https:' && u.hostname) return null;
  if (u.protocol === 'http:' && LOOPBACK.has(u.hostname)) return null;
  if (/^[a-z][a-z0-9+-]*(\.[a-z0-9+-]+)+:$/.test(u.protocol)) return null;
  return t('dev.app_uri_https');
}

export function AppsTab({ o, onNew, onEdit, onChange }: { o: Modules & { apps: AppRow[] }; onNew: () => void; onEdit: (a: AppRow) => void; onChange: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const [drop, setDrop] = useState<AppRow | null>(null);
  const [roll, setRoll] = useState<AppRow | null>(null);
  const [rolled, setRolled] = useState<{ app: AppRow; secret: string } | null>(null);
  const [people, setPeople] = useState<AppRow | null>(null);
  return (
    <div className="dev-apps">
      <section className="panel">
        <div className="dev-panel-head">
          <div>
            <h2 className="dev-h2">{t('dev.apps')}</h2>
            <p className="muted small">{t('dev.apps_lead')}</p>
          </div>
        </div>
        {!o.apps.length ? (
          <EmptyState
            icon="apps"
            title={t('dev.no_apps')}
            body={t('dev.no_apps_body')}
            action={
              <Button variant="primary" icon="plus" onClick={onNew}>
                {t('dev.new_app')}
              </Button>
            }
          />
        ) : (
          <Table
            head={[t('dev.app'), t('dev.app_kind'), t('dev.access'), t('dev.parts'), t('dev.app_people'), t('dev.state'), '']}
            left={[1, 2]}
            phoneHide={[2, 3, 4, 5]}
            rows={o.apps.map((a) => ({
              key: a.id,
              cells: [
                <span className={`dev-key-cell${a.active ? '' : ' dev-dim'}`}>
                  <strong>{a.name}</strong>
                  <code className="dev-prefix" title={a.client_id}>
                    {a.client_id}
                  </code>
                  <span className="muted small">
                    {[a.user_name ? t('dev.made_by', { name: a.user_name }) : null, a.last_used_at ? t('dev.app_used', { when: since(a.last_used_at) }) : null].filter(Boolean).join(' · ')}
                  </span>
                </span>,
                <span className="dev-app-kind">
                  <Icon name={a.public ? 'phone' : 'cpu'} size={14} />
                  {a.public ? t('dev.app_public') : t('dev.app_server')}
                </span>,
                <AccessBadge access={a.access} />,
                <ModuleChips modules={a.modules} labels={o.modules} />,
                a.connected ? (
                  <Button size="sm" variant="ghost" icon="users" onClick={() => setPeople(a)}>
                    {tn('dev.app_people_n', a.connected)}
                  </Button>
                ) : (
                  <span className="muted">{t('dev.app_nobody')}</span>
                ),
                a.active ? <StatusBadge tone="ok" icon="checkCircle" label={t('dev.active')} /> : <StatusBadge tone="neutral" icon="pause" label={t('dev.app_off')} />,
                <span className="dev-row-acts">
                  <IconButton icon="edit" label={t('dev.edit_n', { name: a.name })} onClick={() => onEdit(a)} />
                  {!a.public ? <IconButton icon="lock" label={t('dev.app_roll_n', { name: a.name })} onClick={() => setRoll(a)} /> : null}
                  <IconButton icon="trash" label={t('dev.delete_n', { name: a.name })} onClick={() => setDrop(a)} />
                </span>,
              ],
            }))}
          />
        )}
      </section>
      <HowAppsConnect sample={o.apps.find((a) => a.active) ?? null} />
      {drop ? (
        <Confirm
          title={t('dev.app_delete_title', { name: drop.name })}
          body={drop.connected ? tn('dev.app_delete_body_n', drop.connected) : t('dev.app_delete_body')}
          confirm={t('dev.delete')}
          danger
          busy={busy}
          onClose={() => setDrop(null)}
          onConfirm={() =>
            void run(async () => {
              await action('developer.delete_app', { id: drop.id });
              setDrop(null);
              await onChange();
            }, t('dev.deleted'))
          }
        />
      ) : null}
      {roll ? (
        <Confirm
          title={t('dev.app_roll_title', { name: roll.name })}
          body={t('dev.app_roll_body')}
          confirm={t('dev.roll')}
          busy={busy}
          onClose={() => setRoll(null)}
          onConfirm={() =>
            void run(async () => {
              const r = await action<{ secret: string }>('developer.app_secret', { id: roll.id });
              setRolled({ app: roll, secret: r.secret });
              setRoll(null);
              await onChange();
            })
          }
        />
      ) : null}
      {rolled ? <Credentials app={rolled.app} secret={rolled.secret} title={t('dev.app_rolled', { name: rolled.app.name })} onClose={() => setRolled(null)} /> : null}
      {people ? <ConnectionsDialog app={people} labels={o.modules} onClose={() => setPeople(null)} onChange={onChange} /> : null}
    </div>
  );
}

/** What a developer needs: the two addresses, and the requests in order. */
function HowAppsConnect({ sample }: { sample: AppRow | null }) {
  const base = origin();
  const id = sample?.client_id ?? 'erpc.…';
  const cb = sample?.redirect_uris[0] ?? 'https://your-app.example.com/callback';
  const authorize = `${base}/oauth/authorize?response_type=code
  &client_id=${id}
  &redirect_uri=${encodeURIComponent(cb)}
  &scope=read
  &state=<random>
  &code_challenge=<BASE64URL(SHA256(verifier))>
  &code_challenge_method=S256`;
  const token = `curl -X POST ${base}/api/oauth/token \\
  -d client_id=${id} \\
  -d client_secret="$CLIENT_SECRET" \\
  -d grant_type=authorization_code \\
  -d code=<code> \\
  -d code_verifier=<verifier> \\
  -d redirect_uri=${cb}

# ${t('dev.app_sample_refresh')}
curl -X POST ${base}/api/oauth/token \\
  -d client_id=${id} \\
  -d client_secret="$CLIENT_SECRET" \\
  -d grant_type=refresh_token \\
  -d refresh_token=erpr.…`;
  return (
    <section className="panel dev-how">
      <h2 className="dev-h2">{t('dev.app_how')}</h2>
      <ol className="dev-app-steps">
        <li>
          <strong>{t('dev.app_step1')}</strong>
          <span className="muted small">{t('dev.app_step1_body')}</span>
          <Code>{authorize}</Code>
        </li>
        <li>
          <strong>{t('dev.app_step2')}</strong>
          <span className="muted small">{t('dev.app_step2_body')}</span>
          <Code>{token}</Code>
        </li>
        <li>
          <strong>{t('dev.app_step3')}</strong>
          <span className="muted small">{t('dev.app_step3_body')}</span>
          <Code>{`curl ${base}/api/records/customer.customer?limit=20 \\\n  -H "Authorization: Bearer erpo.…"`}</Code>
        </li>
      </ol>
      <p className="muted small">
        {t('dev.app_meta')} <code>{`${base}/.well-known/oauth-authorization-server`}</code>
      </p>
    </section>
  );
}

/** The client id and, this once, the secret. */
function Credentials({ app, secret, title, onClose }: { app: Pick<AppRow, 'client_id' | 'public'>; secret: string | null; title: string; onClose: () => void }) {
  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={onClose}>
          {secret ? t('dev.key_saved') : t('dev.done')}
        </Button>
      }
    >
      <div className="form-stack">
        <Field label={t('dev.app_client_id')}>
          <div className="dev-secret">
            <code>{app.client_id}</code>
            <CopyButton text={app.client_id} />
          </div>
        </Field>
        {secret ? (
          <>
            <p className="callout callout-warn">
              <Icon name="lock" size={18} />
              <span>{t('dev.app_secret_once')}</span>
            </p>
            <Field label={t('dev.app_client_secret')}>
              <div className="dev-secret">
                <code>{secret}</code>
                <CopyButton text={secret} />
              </div>
            </Field>
          </>
        ) : (
          <p className="callout callout-info">
            <Icon name="info" size={18} />
            <span>{t('dev.app_public_body')}</span>
          </p>
        )}
        <dl className="dev-app-urls">
          <div>
            <dt>{t('dev.app_authorize_url')}</dt>
            <dd>
              <code>{`${origin()}/oauth/authorize`}</code>
            </dd>
          </div>
          <div>
            <dt>{t('dev.app_token_url')}</dt>
            <dd>
              <code>{`${origin()}/api/oauth/token`}</code>
            </dd>
          </div>
        </dl>
      </div>
    </Dialog>
  );
}

export function AppDialog({
  modules,
  app,
  onClose,
  onSaved,
}: {
  modules: { name: string; label: string }[];
  app: AppRow | null;
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}) {
  const { busy, run } = useSave();
  const [name, setName] = useState(app?.name ?? '');
  const [description, setDescription] = useState(app?.description ?? '');
  const [homepage, setHomepage] = useState(app?.homepage ?? '');
  const [isPublic, setPublic] = useState(app?.public ?? false);
  const [uris, setUris] = useState<string[]>(app?.redirect_uris.length ? app.redirect_uris : ['']);
  const [access, setAccess] = useState<Access>(app?.access ?? 'read');
  const [all, setAll] = useState(app ? !app.modules : false);
  const [picked, setPicked] = useState<string[]>(app?.modules ?? []);
  const [active, setActive] = useState(app?.active ?? true);
  const [made, setMade] = useState<{ app: AppRow; secret: string | null } | null>(null);
  const problems = useMemo(() => uris.map(redirectProblem), [uris]);
  const filled = uris.map((u) => u.trim()).filter(Boolean);
  const ready = name.trim() && filled.length && problems.every((p) => !p) && (all || picked.length);
  if (made) return <Credentials app={made.app} secret={made.secret} title={t('dev.app_made', { name: made.app.name })} onClose={onClose} />;
  return (
    <Dialog
      title={app ? t('dev.app_edit', { name: app.name }) : t('dev.new_app')}
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon={app ? 'check' : 'apps'}
            busy={busy}
            disabled={!ready}
            onClick={() =>
              void run(async () => {
                const body = {
                  ...(app ? { id: app.id, active } : { public: isPublic }),
                  name,
                  description: description.trim() || null,
                  homepage: homepage.trim() || null,
                  redirect_uris: filled,
                  access,
                  modules: all ? null : picked,
                };
                const r = await action<{ app: AppRow; secret?: string }>('developer.save_app', body);
                await onSaved();
                if (app) onClose();
                else setMade({ app: r.app, secret: r.secret ?? null });
              }, app ? t('dev.saved') : undefined)
            }
          >
            {app ? t('common.save') : t('dev.app_register')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('dev.app_name')} hint={t('dev.app_name_hint')} required>
          <input className="input" value={name} maxLength={80} placeholder={t('dev.app_name_ph')} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label={t('dev.app_description')} hint={t('dev.app_description_hint')}>
          <textarea className="input" rows={2} maxLength={500} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label={t('dev.app_homepage')}>
          <input className="input" type="url" inputMode="url" value={homepage} maxLength={300} placeholder="https://" onChange={(e) => setHomepage(e.target.value)} />
        </Field>
        {!app ? (
          <fieldset className="dev-fieldset">
            <legend className="field-label">{t('dev.app_kind')}</legend>
            <div className="dev-choice">
              {([false, true] as const).map((p) => (
                <label key={String(p)} className={`dev-choice-card${isPublic === p ? ' on' : ''}`}>
                  <input type="radio" name="dev-app-kind" checked={isPublic === p} onChange={() => setPublic(p)} />
                  <Icon name={p ? 'phone' : 'cpu'} size={18} />
                  <span>
                    <strong>{p ? t('dev.app_public') : t('dev.app_server')}</strong>
                    <span className="muted small">{p ? t('dev.app_public_hint') : t('dev.app_server_hint')}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}
        <fieldset className="dev-fieldset">
          <legend className="field-label">{t('dev.app_uris')}</legend>
          <p className="muted small">{t('dev.app_uris_hint')}</p>
          <div className="dev-uris">
            {uris.map((u, i) => (
              <div key={i} className="dev-uri-row">
                <Field label={t('dev.app_uri_n', { n: i + 1 })} error={problems[i]}>
                  <input
                    className="input mono"
                    value={u}
                    maxLength={500}
                    inputMode="url"
                    placeholder="https://your-app.example.com/callback"
                    onChange={(e) => setUris(uris.map((x, j) => (j === i ? e.target.value : x)))}
                  />
                </Field>
                {uris.length > 1 ? <IconButton icon="x" label={t('dev.app_uri_remove', { n: i + 1 })} onClick={() => setUris(uris.filter((_, j) => j !== i))} /> : null}
              </div>
            ))}
          </div>
          {uris.length < 10 ? (
            <Button size="sm" variant="ghost" icon="plus" onClick={() => setUris([...uris, ''])}>
              {t('dev.app_uri_add')}
            </Button>
          ) : null}
        </fieldset>
        <fieldset className="dev-fieldset">
          <legend className="field-label">{t('dev.app_most')}</legend>
          <div className="dev-choice">
            {(['read', 'write'] as const).map((a) => (
              <label key={a} className={`dev-choice-card${access === a ? ' on' : ''}`}>
                <input type="radio" name="dev-app-access" checked={access === a} onChange={() => setAccess(a)} />
                <Icon name={a === 'read' ? 'eye' : 'edit'} size={18} />
                <span>
                  <strong>{a === 'read' ? t('dev.read_only') : t('dev.read_write')}</strong>
                  <span className="muted small">{a === 'read' ? t('dev.app_read_hint') : t('dev.app_write_hint')}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <PartsPicker modules={modules} all={all} setAll={setAll} picked={picked} setPicked={setPicked} hint={t('dev.app_parts_hint')} />
        {app ? (
          <div className="setting-row">
            <div>
              <strong>{t('dev.app_on')}</strong>
              <p className="muted">{t('dev.app_on_hint')}</p>
            </div>
            <Switch checked={active} onChange={setActive} label={t('dev.app_on')} />
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

function ConnectionsDialog({ app, labels, onClose, onChange }: { app: AppRow; labels: { name: string; label: string }[]; onClose: () => void; onChange: () => Promise<unknown> }) {
  const { data, reload } = useAction<Connection[]>('developer.app_connections', { app: app.id });
  const { busy, run } = useSave();
  const [cut, setCut] = useState<Connection | null>(null);
  return (
    <Dialog title={t('dev.app_people_title', { name: app.name })} size="lg" onClose={onClose}>
      {!data ? (
        <Skeleton h={160} />
      ) : !data.length ? (
        <EmptyState icon="users" title={t('dev.app_nobody')} />
      ) : (
        <Table
          head={[t('dev.app_person'), t('dev.access'), t('dev.parts'), t('dev.app_since'), t('dev.last_used'), '']}
          left={[1, 2]}
          phoneHide={[2, 3, 4]}
          rows={data.map((c) => ({
            key: c.id,
            cells: [
              <strong>{c.user_name}</strong>,
              <AccessBadge access={c.access} />,
              <ModuleChips modules={c.modules} labels={labels} />,
              day(c.created_at),
              c.last_used_at ? <span title={c.last_ip ?? undefined}>{since(c.last_used_at)}</span> : <span className="muted">{t('dev.never')}</span>,
              <IconButton icon="x" label={t('dev.app_disconnect_n', { name: c.user_name })} onClick={() => setCut(c)} />,
            ],
          }))}
        />
      )}
      {cut ? (
        <Confirm
          title={t('dev.app_disconnect_title', { name: cut.user_name })}
          body={t('dev.app_disconnect_body', { app: app.name })}
          confirm={t('dev.app_disconnect')}
          danger
          busy={busy}
          onClose={() => setCut(null)}
          onConfirm={() =>
            void run(async () => {
              await action('developer.disconnect', { id: cut.id });
              setCut(null);
              await Promise.all([reload(), onChange()]);
            }, t('dev.app_disconnected'))
          }
        />
      ) : null}
    </Dialog>
  );
}

// ---- Settings → Security: the apps this person connected -----------------------------------------

interface MyApp {
  id: string;
  name: string;
  homepage: string | null;
  active: boolean;
  access: Access;
  modules: { name: string; label: string }[] | null;
  created_at: string;
  last_used_at: string | null;
}

/** Shown only to someone who has connected an app. */
export function ConnectedApps() {
  const [list, setList] = useState<MyApp[] | null>(null);
  const { busy, run } = useSave();
  const [cut, setCut] = useState<MyApp | null>(null);
  const load = () => action<MyApp[]>('developer.my_apps').then(setList, () => setList([]));
  useEffect(() => {
    void load();
  }, []);
  if (!list?.length) return null;
  return (
    <section className="card sec-card sec-apps-card" aria-labelledby="sec-apps">
      <div className="card-head">
        <h2 id="sec-apps">
          <Icon name="apps" size={18} /> {t('sec.apps_title')}
        </h2>
      </div>
      <p className="muted">{t('sec.apps_body')}</p>
      <ul className="sec-apps">
        {list.map((a) => (
          <li key={a.id}>
            <span className="sec-app-icon" aria-hidden="true">
              {a.name.slice(0, 1).toUpperCase()}
            </span>
            <span className="sec-app-text">
              <strong>
                {a.homepage ? (
                  <a href={a.homepage} target="_blank" rel="noreferrer noopener">
                    {a.name}
                  </a>
                ) : (
                  a.name
                )}
              </strong>
              <span className="muted small">
                {[
                  a.access === 'write' ? t('dev.read_write') : t('dev.read_only'),
                  a.modules ? a.modules.map((m) => m.label).join(', ') : t('dev.all_parts'),
                  t('sec.apps_since', { day: day(a.created_at) }),
                  a.last_used_at ? t('dev.app_used', { when: since(a.last_used_at) }) : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </span>
            <Button size="sm" variant="secondary" onClick={() => setCut(a)}>
              {t('dev.app_disconnect')}
            </Button>
          </li>
        ))}
      </ul>
      {cut ? (
        <Confirm
          title={t('sec.apps_cut_title', { name: cut.name })}
          body={t('sec.apps_cut_body')}
          confirm={t('dev.app_disconnect')}
          danger
          busy={busy}
          onClose={() => setCut(null)}
          onConfirm={() =>
            void run(async () => {
              await action('developer.disconnect', { id: cut.id });
              setCut(null);
              await load();
            }, t('dev.app_disconnected'))
          }
        />
      ) : null}
    </section>
  );
}
